import { useState } from 'react';
import {
  ExternalLink,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  UserCheck,
  ArrowUp,
  Zap,
  Shield,
  Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useSubscription, createPortalSession, createCheckoutSession } from '@/lib/billing-query';
import type { PlanTier, SubscriptionStatus } from '@/lib/billing.types';

// ───── Plan metadata ──────────────────────────────────────────────────

const PLAN_META: Record<PlanTier, { label: string; color: string; icon: typeof Zap }> = {
  solo: { label: 'Solo', color: 'bg-slate-500', icon: Zap },
  pro: { label: 'Pro', color: 'bg-blue-600', icon: Shield },
  enterprise: { label: 'Enterprise', color: 'bg-purple-600', icon: Building2 },
};

const STATUS_META: Record<SubscriptionStatus, { label: string; color: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2 }> = {
  active: { label: 'Active', color: 'default', icon: CheckCircle2 },
  trialing: { label: 'Trial', color: 'secondary', icon: Clock },
  past_due: { label: 'Past Due', color: 'destructive', icon: AlertTriangle },
  canceled: { label: 'Canceled', color: 'outline', icon: XCircle },
};

const PLAN_TIERS: PlanTier[] = ['solo', 'pro', 'enterprise'];

const PLAN_DETAILS: Record<PlanTier, { users: string; clients: string; price: string }> = {
  solo: { users: '1 user', clients: '50 clients', price: 'Free / Trial' },
  pro: { users: '5 users', clients: '200 clients', price: '—' },
  enterprise: { users: 'Unlimited', clients: 'Unlimited', price: '—' },
};

// ───── Component ──────────────────────────────────────────────────────

export function Billing() {
  const { data: sub, isLoading, error, refetch } = useSubscription();
  const [actionLoading, setActionLoading] = useState<'portal' | PlanTier | null>(null);

  const handlePortal = async () => {
    setActionLoading('portal');
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch {
      setActionLoading(null);
    }
  };

  const handleCheckout = async (tier: PlanTier) => {
    setActionLoading(tier);
    try {
      const { url } = await createCheckoutSession(tier);
      window.location.href = url;
    } catch {
      setActionLoading(null);
    }
  };

  // ─── Derived state ─────────────────────────────────────────────────

  const planTier: PlanTier = sub?.planTier ?? 'solo';
  const planMeta = PLAN_META[planTier];
  const PlanIcon = planMeta.icon;
  const statusMeta = sub ? STATUS_META[sub.status] : null;

  const usage = sub?.usage ?? { users: 0, clients: 0 };
  const limits = sub?.planLimits ?? { maxUsers: null, maxClients: null };

  return (
    <section className="mx-auto max-w-4xl px-6 py-10 space-y-8">

        {/* ── Current Plan Card ───────────────────────────────────── */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-3">
                <div className={`size-10 rounded-lg ${planMeta.color} text-white flex items-center justify-center`}>
                  <PlanIcon className="size-5" />
                </div>
                <span>{planMeta.label} Plan</span>
                {statusMeta && (
                  <Badge variant={statusMeta.color} className="ml-2">
                    <statusMeta.icon className="size-3 mr-1 inline" />
                    {statusMeta.label}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                {sub?.currentPeriodStart
                  ? `Current period: ${new Date(sub.currentPeriodStart).toLocaleDateString()} – ${sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : '—'}`
                  : 'No active subscription period'}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Cancelation warning */}
            {sub?.cancelAtPeriodEnd && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm">
                <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <strong>Cancelation scheduled</strong>
                  <p className="text-muted-foreground mt-1">
                    Your subscription will be canceled at the end of the current billing period.
                    You can re-activate from the Stripe Customer Portal.
                  </p>
                </div>
              </div>
            )}

            {/* Past due warning */}
            {sub?.status === 'past_due' && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm">
                <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <strong>Payment required</strong>
                  <p className="text-muted-foreground mt-1">
                    Your latest invoice payment failed. Please update your payment method
                    to avoid service interruption.
                  </p>
                </div>
              </div>
            )}

            {/* Usage bars */}
            <div className="space-y-4">
              <UsageBar
                label="Users"
                icon={<Users className="size-4" />}
                current={usage.users}
                max={limits.maxUsers}
              />
              <UsageBar
                label="Clients"
                icon={<UserCheck className="size-4" />}
                current={usage.clients}
                max={limits.maxClients}
              />
            </div>
          </CardContent>

          <CardFooter className="flex flex-wrap gap-3">
            <Button
              variant="default"
              onClick={handlePortal}
              disabled={actionLoading === 'portal'}
            >
              {actionLoading === 'portal' ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <ExternalLink className="size-4 mr-2" />
              )}
              Manage Billing
            </Button>

            {/* Refresh subscription data */}
            <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
              {isLoading ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
              Refresh
            </Button>
          </CardFooter>
        </Card>

        {/* ── Plan Comparison ──────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Available Plans</CardTitle>
            <CardDescription>
              Compare features across plans. Upgrade anytime from the Stripe Customer Portal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {PLAN_TIERS.map((tier) => {
                const meta = PLAN_META[tier];
                const TIcon = meta.icon;
                const details = PLAN_DETAILS[tier];
                const isCurrent = tier === planTier;

                return (
                  <div
                    key={tier}
                    className={`relative rounded-lg border p-5 transition-colors ${
                      isCurrent
                        ? 'border-primary ring-1 ring-primary bg-primary/5'
                        : 'bg-card hover:border-muted-foreground/30'
                    }`}
                  >
                    {isCurrent && (
                      <Badge variant="default" className="absolute -top-2.5 right-3 text-xs">
                        Current
                      </Badge>
                    )}

                    <div className={`size-9 rounded-lg ${meta.color} text-white flex items-center justify-center mb-3`}>
                      <TIcon className="size-4" />
                    </div>
                    <h3 className="font-semibold text-base mb-1">{meta.label}</h3>
                    <p className="text-lg font-bold mb-3">{details.price}</p>

                    <ul className="space-y-1.5 text-sm text-muted-foreground mb-4">
                      <li className="flex items-center gap-2">
                        <Users className="size-3.5 shrink-0" />
                        {details.users}
                      </li>
                      <li className="flex items-center gap-2">
                        <UserCheck className="size-3.5 shrink-0" />
                        {details.clients}
                      </li>
                    </ul>

                    {!isCurrent && (
                      <Button
                        size="sm"
                        className="w-full"
                        variant={tier === 'enterprise' ? 'default' : 'outline'}
                        onClick={() => handleCheckout(tier)}
                        disabled={actionLoading === tier}
                      >
                        {actionLoading === tier ? (
                          <Loader2 className="size-4 mr-1 animate-spin" />
                        ) : (
                          <ArrowUp className="size-4 mr-1" />
                        )}
                        {tier === 'pro' ? 'Upgrade' : 'Contact Us'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Error state */}
        {error && (
          <Card className="border-destructive/20">
            <CardContent className="pt-6 text-sm text-destructive">
              <AlertTriangle className="size-4 inline mr-2" />
              Could not load subscription details. {error instanceof Error ? error.message : ''}
            </CardContent>
          </Card>
        )}
    </section>
  );
}

// ───── Usage Bar ──────────────────────────────────────────────────────

function UsageBar({
  label,
  icon,
  current,
  max,
}: {
  label: string;
  icon: React.ReactNode;
  current: number;
  max: number | null;
}) {
  const isUnlimited = max === null;
  const pct = isUnlimited ? 0 : max > 0 ? Math.round((current / max) * 100) : 0;
  const isOver = !isUnlimited && current >= max;

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="flex items-center gap-1.5 font-medium">
          {icon}
          {label}
        </span>
        <span className="text-muted-foreground">
          {current} / {isUnlimited ? '∞' : max}
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        {!isUnlimited && (
          <div
            className={`h-full rounded-full transition-all ${
              isOver ? 'bg-destructive' : pct > 80 ? 'bg-amber-500' : 'bg-primary'
            }`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        )}
      </div>
      {isOver && (
        <p className="text-xs text-destructive mt-1 flex items-center gap-1">
          <AlertTriangle className="size-3" />
          {label} limit reached. Upgrade your plan to add more.
        </p>
      )}
    </div>
  );
}
